import { supabase, isSupabaseConfigured } from './supabase';
import type { Price, PriceUpdate } from './database.types';

// Get all prices
export async function getPrices(): Promise<Price[]> {
  if (!isSupabaseConfigured()) {
    return [];
  }

  const { data, error } = await supabase
    .from('prices')
    .select('*')
    .order('name');

  if (error) {
    console.error('Error fetching prices:', error);
    return [];
  }

  return (data || []) as Price[];
}

// Get price by name
export async function getPriceByName(name: string): Promise<Price | null> {
  if (!isSupabaseConfigured()) return null;

  const { data, error } = await supabase
    .from('prices')
    .select('*')
    .eq('name', name)
    .eq('is_active', true)
    .single();

  if (error) {
    console.error('Error fetching price:', error);
    return null;
  }

  return data as Price;
}

// Update price
export async function updatePrice(
  id: string,
  updates: PriceUpdate
): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase is not configured' };
  }

  const { error } = await supabase
    .from('prices')
    .update(updates)
    .eq('id', id);

  if (error) {
    console.error('Error updating price:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

// Create price
export async function createPrice(
  name: string,
  description: string,
  price: number
): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase is not configured' };
  }

  const { error } = await supabase
    .from('prices')
    .insert({ name, description, price });

  if (error) {
    console.error('Error creating price:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

// Delete price
export async function deletePrice(id: string): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase is not configured' };
  }

  const { error } = await supabase
    .from('prices')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting price:', error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

// =====================================================
// Price calculation helpers (synced with bookingService)
// =====================================================

export interface PriceConfig {
  basePriceUpTo8Days: number;
  priceDay9: number;
  pricePerAdditionalDay: number;
  nightSurcharge: number;
  extras: {
    charging: number;
    cancel: number;
  };
}

export const DEFAULT_PRICE_CONFIG: PriceConfig = {
  basePriceUpTo8Days: 99,
  priceDay9: 100,
  pricePerAdditionalDay: 10,
  nightSurcharge: 80,
  extras: {
    charging: 45,
    cancel: 5,
  },
};

export const PAYPAL_PAYMENT_FEE = 5;

export function calculatePaymentSurcharge(paymentMethod: 'cash' | 'paypal'): number {
  return paymentMethod === 'paypal' ? PAYPAL_PAYMENT_FEE : 0;
}

const DEFAULT_PRICE_MAP: Record<string, number> = {
  base_price_1_8: DEFAULT_PRICE_CONFIG.basePriceUpTo8Days,
  price_day_9: DEFAULT_PRICE_CONFIG.priceDay9,
  price_per_additional_day: DEFAULT_PRICE_CONFIG.pricePerAdditionalDay,
  night_surcharge: DEFAULT_PRICE_CONFIG.nightSurcharge,
  extra_charging: DEFAULT_PRICE_CONFIG.extras.charging,
  extra_cancel: DEFAULT_PRICE_CONFIG.extras.cancel,
};

const REQUIRED_PRICE_KEYS = [
  'base_price_1_8',
  'price_day_9',
  'price_per_additional_day',
  'night_surcharge',
  'extra_charging',
  'extra_cancel',
] as const;

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const buildPriceConfig = (prices: Record<string, number>, fallback: PriceConfig): PriceConfig => ({
  basePriceUpTo8Days: typeof prices.base_price_1_8 === 'number' ? prices.base_price_1_8 : fallback.basePriceUpTo8Days,
  priceDay9: typeof prices.price_day_9 === 'number' ? prices.price_day_9 : fallback.priceDay9,
  pricePerAdditionalDay: typeof prices.price_per_additional_day === 'number' ? prices.price_per_additional_day : fallback.pricePerAdditionalDay,
  nightSurcharge: typeof prices.night_surcharge === 'number' ? prices.night_surcharge : fallback.nightSurcharge,
  extras: {
    charging: typeof prices.extra_charging === 'number' ? prices.extra_charging : fallback.extras.charging,
    cancel: typeof prices.extra_cancel === 'number' ? prices.extra_cancel : fallback.extras.cancel,
  },
});

const getMissingPriceKeys = (prices: Record<string, number>): string[] => {
  return REQUIRED_PRICE_KEYS.filter((key) => typeof prices[key] !== 'number');
};

// Get active prices as a map
export async function getActivePricesMap(): Promise<Record<string, number>> {
  if (!isSupabaseConfigured()) {
    // Return default prices if Supabase is not configured
    return { ...DEFAULT_PRICE_MAP };
  }

  const { data, error } = await supabase
    .from('prices')
    .select('name, price')
    .eq('is_active', true);

  if (error) {
    console.error('Error fetching prices:', error);
    return {};
  }

  const priceMap: Record<string, number> = {};
  data?.forEach((p: { name: string; price: number | string }) => {
    const numericValue = toNumber(p.price);
    if (numericValue !== null) {
      priceMap[p.name] = numericValue;
    }
  });

  return priceMap;
}

export async function loadPriceConfig(): Promise<PriceConfig> {
  const prices = await getActivePricesMap();

  return buildPriceConfig(prices, DEFAULT_PRICE_CONFIG);
}

export async function loadPriceConfigStrict(): Promise<PriceConfig> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase ist nicht konfiguriert.');
  }

  const prices = await getActivePricesMap();
  const missingKeys = getMissingPriceKeys(prices);

  if (missingKeys.length > 0) {
    throw new Error(`Preiskonfiguration unvollständig: ${missingKeys.join(', ')}`);
  }

  return buildPriceConfig(prices, DEFAULT_PRICE_CONFIG);
}

// Calculate base price based on days
export function calculateBasePrice(days: number, config: PriceConfig): number {
  if (days <= 0) return 0;
  if (days <= 8) return config.basePriceUpTo8Days;
  if (days === 9) return config.priceDay9;
  // Day 10+ = Day 9 price + (days - 9) * additional day price
  return config.priceDay9 + (days - 9) * config.pricePerAdditionalDay;
}
